import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import { OHS_HOST, PAYMENT_URL } from '@/common';
import { useGlobalModal } from '@/provider';
import { getAppStoreAction, getPlayStoreAction } from '../utils/customLink';
import { useOhousePayConfigPage } from './useOhousePayConfigPage';
import { usePlatformInfo } from './usePlatformInfo';

export const CTA_URLS = {
  PLAY_STORE_URL:
    'https://play.google.com/store/apps/details?id=net.bucketplace',
  APP_STORE_URL:
    'itms-apps://itunes.apple.com/app/1008236892?click_action_type=external',
};

interface UseGetCustomLinkParams {
  isLoggedIn: boolean;
}

export interface CustomLinkProps {
  href: string;
  onClick?: (event: React.MouseEvent) => void | Promise<void>;
}

export const useGetCustomLink = ({
  isLoggedIn,
}: UseGetCustomLinkParams): ((to: string) => CustomLinkProps | null) => {
  const { asPath } = useRouter();
  const { openConfirmModal } = useGlobalModal();
  const { openPage } = useOhousePayConfigPage({ pageType: 'home' });
  const { osType } = usePlatformInfo();

  const onRedirectToSignIn = useCallback(async (): Promise<void> => {
    const path = `/events${asPath}`;
    const signInUrl = `${OHS_HOST}/users/sign_in?redirect_to=${encodeURIComponent(path)}`;

    if (await openConfirmModal({ message: '로그인 후 이용이 가능합니다.' })) {
      window.location.href = signInUrl;
    }
  }, [asPath, openConfirmModal]);

  return useCallback(
    (to: string): CustomLinkProps | null => {
      switch (true) {
        case to.includes(`${PAYMENT_URL}/ohouse-pay`):
          return {
            href: 'javascript:;',
            onClick: async (event: React.MouseEvent) => {
              event.preventDefault();

              if (!isLoggedIn) {
                await onRedirectToSignIn();
                return;
              }

              openPage();
            },
          };
        case to.includes(CTA_URLS.PLAY_STORE_URL):
          return getPlayStoreAction({ osType, to });
        case to.includes(CTA_URLS.APP_STORE_URL):
          return getAppStoreAction({ osType, to });
        default:
          return null;
      }
    },
    [isLoggedIn, onRedirectToSignIn, openPage, osType],
  );
};
